// Type Curve · TC-1/TC-2 — AI (cluster) type-curve HERO container.
//
// This container still OWNS the type-curve data flow (recommended DCA fit +
// user-tuned DCA fit + Edit Parameters + Validated-to-Save) exactly as before.
// TC-2 change: instead of rendering a separate "Type Curve" chart and letting a
// sibling render a separate "Uncertainty" chart, it ALSO pulls the performance
// (P50 fit + P10/P90 band) here and feeds everything into ONE merged
// TypeCurveHeroChart. The old ClusterUncertaintyChart is therefore no longer
// mounted from the page.
//
// The active cluster is now driven from redux (activeClusterId) so the
// permanent right sidebar (TC-3) and this HERO stay in sync.
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

import {
  DECLINE_PHASE_OPTIONS,
  NormalizeOptions,
  DeclineModelOptions,
} from "app/codes";

import {
  setDeclinePhase,
  selectDeclinePhase,
  selectProjectClusterDataItems,
  setProjectClusterDataRecommParamsWithIndex,
  setProjectClusterDataUserParamsWithIndex,
  selectGroupNormalizeBy,
  setGroupNormalizeBy,
  selectClusterNormalizeBy,
  selectApplyUserParams,
  setApplyUserParams,
  setClusterNormalizeBy,
  selectDcaAllModelsParams,
  setDcaAllModelsParams,
  selectActiveClusterId,
  setActiveClusterId,
} from "features/decline_analysis/typeCurveProjectSlice";

import {
  getDcaRecommends,
  getDcaUserTuneData,
  getPerformance,
} from "service/typeCurve";

import TypeCurveHeroChart from "components/charts/TypeCurveHeroChart";
import { useUserPreference } from "components/charts/UserPreference";

function AiTypeCurveChart(props) {
  const { submitTCProject } = props;
  const { loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];

  const declinePhase = useSelector(selectDeclinePhase);
  const projectClusterDataItems = useSelector(
    selectProjectClusterDataItems,
    shallowEqual
  );
  const normalizedByParam = useSelector(selectClusterNormalizeBy);
  const applyUserParams = useSelector(selectApplyUserParams);
  const allModelsParameters = useSelector(selectDcaAllModelsParams);
  const dispatch = useDispatch();

  // TC-3: the selected cluster is shared with the sidebar via redux.
  const activeClusterId = useSelector(selectActiveClusterId);
  const selectedClusterIndex =
    activeClusterId == null ? null : Number(activeClusterId);

  const normalizeBy = useSelector(selectGroupNormalizeBy);

  const [UserPreference, showUserPreference] = useUserPreference();

  // historical (normalized-avg) production data
  const [historicalData, setHistoricalData] = useState(null);
  // recommended-DCA fitted line
  const [recommData, setRecommData] = useState(null);
  // user-tuned DCA fitted line
  const [userDcaData, setUserDcaData] = useState(null);
  // TC-2: merged performance (P50 fit + P10/P90 band) for this cluster
  const [performance, setPerformance] = useState(null);

  const updateRecommData = useCallback(
    (cluserIndex, result) => {
      let history = result.history;
      setHistoricalData(history);

      let recommends = result.dca;
      let bestModel = recommends.best_model;
      setRecommData(result.dca.fitted);

      dispatch(
        setProjectClusterDataRecommParamsWithIndex({
          index: cluserIndex,
          recommParams: {
            decline_phase: declinePhase,
            normalized_by: normalizedByParam,
            dca_model_type: bestModel,
            recomm_ip_ratio: recommends.di,
            recomm_initial_decline_rate: recommends.qi,
            recomm_coefficient_b: recommends.b,
          },
        })
      );
    },
    [dispatch, declinePhase, normalizedByParam]
  );

  const getDcaUserTuneDataCallback = useCallback(
    (cluserIndex, projectClusterDataItem) => {
      let projectCluster = projectClusterDataItem.project_cluster;
      const record = projectCluster?.records?.find(
        (item) => item.decline_phase === declinePhase
      );
      let declineModel = record.user_decline_model;
      let wellIds = projectClusterDataItem.cluster_wells;
      if (!declineModel || !wellIds || wellIds.length === 0) {
        console.error("declineModel, wellIds:", declineModel, wellIds);
        return;
      }

      let decline_rate =
        record.user_initial_decline_rate || record.recomm_initial_decline_rate;
      let ip_ratio = record.user_ip_ratio || record.recomm_ip_ratio;
      let b = record.user_coefficient_b || record.recomm_coefficient_b;

      if (typeof decline_rate != "number") {
        decline_rate = parseFloat(decline_rate);
        ip_ratio = parseFloat(ip_ratio);
        b = parseFloat(b);
      }

      if (declineModel === "AI" || (declineModel === "HYP" && b === 0)) {
        console.error("declineModel, b:", declineModel, b);
        return;
      }
      showLoadingIcon && showLoadingIcon();
      getDcaUserTuneData(
        {
          uwis: wellIds,
          decline_phase: declinePhase || "oil",
          decline_model: declineModel,
          normalize_by: normalizedByParam,
          decline_rate: decline_rate,
          ip_ratio: ip_ratio,
          b: b,
        },
        (result) => {
          if (!result || !result.dca || !result.dca.fitted) {
            hideLoadingIcon && hideLoadingIcon();
            return;
          }
          setUserDcaData(result.dca.fitted);
          hideLoadingIcon && hideLoadingIcon();
        },
        (error) => {
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    },
    [declinePhase, normalizedByParam, showLoadingIcon, hideLoadingIcon]
  );

  const getDcaRecommendDataCallback = useCallback(
    (cluserIndex, projectClusterDataItem) => {
      let wellIds = projectClusterDataItem.cluster_wells;
      if (wellIds.length === 0) {
        setHistoricalData(null);
        return;
      }

      showLoadingIcon && showLoadingIcon();
      getDcaRecommends(
        {
          uwis: wellIds,
          decline_phase: declinePhase,
          normalize_by: normalizedByParam,
        },
        (result) => {
          updateRecommData(cluserIndex, result);
          let allModels = [...result.other_models];
          let { best_model, b, di, qi } = result.dca;
          allModels.push({
            model_name: best_model,
            model_params: {
              qi: qi,
              di: di,
              b: b,
            },
          });
          dispatch(setDcaAllModelsParams(allModels));
          hideLoadingIcon && hideLoadingIcon();
        },
        (error) => {
          console.error("getDcaRecommends error! ", error);
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    },
    [
      dispatch,
      declinePhase,
      normalizedByParam,
      hideLoadingIcon,
      showLoadingIcon,
      updateRecommData,
    ]
  );

  // TC-2: performance (P50 type well + P10/P90 band) for the merged HERO chart.
  // Same endpoint the old ClusterUncertaintyChart used — we just consume it here.
  const getPerformanceCallback = useCallback(
    (projectClusterDataItem, phase, normBy) => {
      let wellIds = projectClusterDataItem?.cluster_wells;
      if (!wellIds || wellIds.length === 0) {
        setPerformance(null);
        return;
      }
      getPerformance(
        {
          uwis: wellIds,
          decline_phase: phase || "oil",
          normalize_by: normBy,
        },
        (result) => {
          setPerformance(
            result && result.dca ? { history: result.history, dca: result.dca } : null
          );
        },
        () => {
          setPerformance(null);
        }
      );
    },
    []
  );

  // Default the active cluster to 0 once clusters exist.
  useEffect(() => {
    if (
      projectClusterDataItems == null ||
      projectClusterDataItems.length === 0
    ) {
      return;
    }
    if (
      selectedClusterIndex == null ||
      selectedClusterIndex > projectClusterDataItems.length - 1
    ) {
      dispatch(setActiveClusterId(0));
    }
  }, [dispatch, projectClusterDataItems, selectedClusterIndex]);

  useEffect(
    () => {
      if (
        selectedClusterIndex == null ||
        !projectClusterDataItems ||
        projectClusterDataItems.length === 0
      ) {
        return;
      }
      setRecommData(null);
      setUserDcaData(null);
      setHistoricalData(null);
      setPerformance(null);
      let currentProjectClusterDataItem =
        projectClusterDataItems[selectedClusterIndex];
      getDcaRecommendDataCallback(
        selectedClusterIndex,
        currentProjectClusterDataItem
      );
      getDcaUserTuneDataCallback(
        selectedClusterIndex,
        currentProjectClusterDataItem
      );
      getPerformanceCallback(
        currentProjectClusterDataItem,
        declinePhase,
        normalizedByParam
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [normalizeBy, selectedClusterIndex, declinePhase]
  );

  useEffect(() => {
    if (
      !applyUserParams ||
      !projectClusterDataItems ||
      projectClusterDataItems.length === 0 ||
      selectedClusterIndex == null
    ) {
      return;
    }
    setUserDcaData(null);
    let currentProjectClusterDataItem =
      projectClusterDataItems[selectedClusterIndex];
    getDcaUserTuneDataCallback(
      selectedClusterIndex,
      currentProjectClusterDataItem
    );

    dispatch(setApplyUserParams(false));
  }, [
    applyUserParams,
    dispatch,
    projectClusterDataItems,
    selectedClusterIndex,
    getDcaUserTuneDataCallback,
  ]);

  function handleDecliePhaseChanged(e) {
    let value = e.target.value;
    dispatch(setDeclinePhase(value));
  }

  function handleSubmitProject() {
    let projectItem = projectClusterDataItems[selectedClusterIndex];
    if (projectItem) {
      submitTCProject({ projectItem: projectItem });
    }
  }

  function handleNormalizeParamChanged(e) {
    const value = e.target.value;
    dispatch(setGroupNormalizeBy(value));
    dispatch(setClusterNormalizeBy(value));
  }

  const getRecommParams = useMemo(() => {
    const projectCluster =
      projectClusterDataItems[selectedClusterIndex]?.project_cluster || {};
    const record =
      projectCluster?.records?.find(
        (item) => item.decline_phase === declinePhase
      ) || {};
    const {
      dca_model_type,
      recomm_ip_ratio,
      recomm_initial_decline_rate,
      recomm_coefficient_b,
    } = record || {};
    return {
      declineModelValue: DeclineModelOptions.find(
        (item) => item.value === dca_model_type
      )?.name,
      ipRatioValue: recomm_ip_ratio?.toFixed(3),
      declineRateValue: recomm_initial_decline_rate?.toFixed(3),
      bValue: recomm_coefficient_b?.toFixed(3),
    };
  }, [declinePhase, selectedClusterIndex, projectClusterDataItems]);

  const getUserParams = useMemo(() => {
    const projectCluster =
      projectClusterDataItems[selectedClusterIndex]?.project_cluster || {};
    const record = projectCluster?.records?.find(
      (item) => item.decline_phase === declinePhase
    );
    const {
      user_decline_model,
      user_ip_ratio,
      user_initial_decline_rate,
      user_coefficient_b,
    } = record || {};
    return {
      declineModel: user_decline_model,
      ipRatio: user_ip_ratio,
      declineRate: user_initial_decline_rate,
      b: user_coefficient_b,
    };
  }, [declinePhase, selectedClusterIndex, projectClusterDataItems]);

  const getYUnit = useCallback((phase) => {
    return DECLINE_PHASE_OPTIONS.find((item) => item.value === phase)?.unit;
  }, []);

  const hasClusters =
    projectClusterDataItems && projectClusterDataItems.length > 0;

  return (
    <>
      <UserPreference
        recommendData={getRecommParams}
        userTuneData={getUserParams}
        defaultParameters={allModelsParameters}
        onClose={(values) => {
          dispatch(
            setProjectClusterDataUserParamsWithIndex({
              index: selectedClusterIndex,
              userParams: {
                decline_phase: declinePhase,
                normalized_by: normalizedByParam,
                user_decline_model: values.declineModel,
                user_ip_ratio: values.ipRatio,
                user_initial_decline_rate: values.declineRate,
                user_coefficient_b: values.b,
              },
            })
          );
          dispatch(setApplyUserParams(true));
        }}
      />
      <div className="cards-container h-100">
        <div className="cards-header d-flex flex-row justify-content-between align-items-center">
          <div className="header-title">
            Type Curve
            {hasClusters && selectedClusterIndex != null
              ? ` · Cluster${selectedClusterIndex}`
              : ""}
          </div>
          <div className="header-menu-container d-flex flex-row align-items-center">
            <div className="d-flex flex-row align-items-center">
              <div>Normalized By</div>
              <select
                className="ms-2"
                value={normalizeBy || "wellbore_length"}
                onChange={handleNormalizeParamChanged}
              >
                {NormalizeOptions.map((item, index) => {
                  return (
                    <option key={index} value={item.value}>
                      {item.name}
                    </option>
                  );
                })}
              </select>
            </div>
            <select
              className="ms-2"
              value={declinePhase || "oil"}
              onChange={handleDecliePhaseChanged}
            >
              {DECLINE_PHASE_OPTIONS.map((item, index) => {
                return (
                  <option key={index} value={item.value}>
                    {item.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="cards-content d-flex flex-column justify-content-between align-items-stretch">
          <div className="flex-grow-1 mb-2" style={{ minHeight: 0 }}>
            <TypeCurveHeroChart
              performance={performance}
              recommData={recommData}
              userDca={userDcaData}
              declinePhase={declinePhase}
              yUnit={getYUnit(declinePhase)}
            />
          </div>
          <div className="d-flex flex-row justify-content-between align-items-center">
            <button
              className="btn-custom"
              onClick={(e) => {
                showUserPreference();
              }}
              disabled={!hasClusters}
            >
              Edit Parameters
            </button>
            <button
              className="btn-custom"
              onClick={(e) => {
                handleSubmitProject();
              }}
              disabled={!hasClusters}
            >
              Validated to Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AiTypeCurveChart;
